<template>
  <div v-rect-loading="loading" class="job-posting-management-container">
    <TableList
      noAction
      class="job-posting-list"
      row-key="id"
      defaultExpandAll
      :titleI18nKey="`${i18nPrefix}.title`"
      :list-data="displayJobPostingList"
      :table-columns="tableColumns"
      :showCreateBtn="false"
      :pagination="pagination"
      :span-method="handleSpanMethod"
      @pagination-change="loadJobPostingList(false)"
    >
      <template #filter>
        <el-input
          v-model="search.keywords"
          clearable
          :placeholder="$t(`${i18nPrefix}.placeholder.searchJobTitle`)"
          @input="handleKeywordsChange"
        ></el-input>
        <el-select
          v-model="search.companyId"
          clearable
          filterable
          @change="loadJobPostingList(true)"
        >
          <el-option
            v-for="(item, index) in companyOptions"
            :key="index"
            :style="{ paddingLeft: item.level * 15 + 'px' }"
            :label="item.i18nName"
            :value="item.id"
          ></el-option>
        </el-select>
        <el-select
          v-model="search.postStatus"
          filterable
          @change="loadJobPostingList(true)"
        >
          <el-option
            v-for="(item, index) in postStatusOptions"
            :key="index"
            :label="item.label"
            :value="item.value"
          ></el-option>
        </el-select>
      </template>
      <template #status="{ row }">
        <div class="status" @click="showJobTaskItemDialog(row)">
          <span :class="row.status === JOB_POSTING_STATUS.PENDING ? 'task-status-open' : 'task-status-finished'">{{ $t(`${i18nPrefix}.label.${row.status === JOB_POSTING_STATUS.PENDING ? 'open' : 'finished' }`) }}</span>
          <span class="iconfont iconxiangxiadejiantou"></span>
        </div>
      </template>
      <template #channel="{ row }">
        <div class="channel-container">
          <div :class="['channel-icon', `channel-${row.channel_key}`]"></div>
        </div>
      </template>
      <template #channelAction="{ row }">
        <div :class="['op-icon', `op-status-${row.operation_type}`]" @click="showJobTaskItemDialog(row)"></div>
      </template>
      <template #job="{ row }">
        <a target="_blank" class="job-link" :href="jobPath(row)">{{ formatJob(row) }}</a>
      </template>
    </TableList>
    <DialogWrapper
      hasCustomTitle
      class="job-task-item-container"
      saveBtnLabelI18nKey="common.btn.save"
      :show.sync="jobPostingDetailDialog.show"
      :loading="loading"
      :hideFooter="jobPostingDetailDialog.hideFooter"
      @save="handleSave">
      <template #title>
        <div class="popover-title">
          <div>{{ jobPostingDetailDialog.jobTitle }}</div>
          <div>{{ jobPostingDetailDialog.companyTitle }}</div>
        </div>
      </template>
      <div>
        <div v-if="jobPostingDetailDialog.task" class="job-task-item">
          <div :class="['op-icon', `op-status-${jobPostingDetailDialog.task.operation_type}`]"></div>
          <div :class="['channel-icon', `channel-${jobPostingDetailDialog.task.channel_key}`]" ></div>
          <el-input
            v-model="jobPostingDetailDialog.task.third_party_link"
            class="third-party-link-input"
            :disabled="jobPostingDetailDialog.task.channel_id === 1 || jobPostingDetailDialog.task.status !== JOB_POSTING_STATUS.PENDING || jobPostingDetailDialog.task.operation_type === OPERATION_TYPE.DELETE"
            :placeholder="$t(`${i18nPrefix}.placeholder.thirdPartyJobLink`)"
          />
          <el-checkbox
            v-model="jobPostingDetailDialog.task.is_finished"
            class="channel-status"
            :disabled="jobPostingDetailDialog.task.status !== JOB_POSTING_STATUS.PENDING"
          ></el-checkbox>
        </div>
      </div>
    </DialogWrapper>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { isEn, getI18nPropertyValue, addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'
import TableList from '@/components/tableList/allowDragSortTable.vue'
import { JOB_POSTING_STATUS, OPERATION_TYPE } from '@/constant/company'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

export default {
  components: {
    TableList,
    DialogWrapper
  },
  data () {
    return {
      JOB_POSTING_STATUS,
      OPERATION_TYPE,
      i18nPrefix: 'admin.company.jobPostingManagement',
      loading: false,
      jobPostingList: [],
      companyOptions: [],
      search: {
        companyId: null,
        keywords: null,
        postStatus: JOB_POSTING_STATUS.PENDING
      },
      pagination: {
        total: 1,
        page: 1,
        pageSize: 10
      },
      jobPostingDetailDialog: {
        show: false,
        jobTitle: null,
        companyTitle: null,
        task: {
          status: null,
          is_finished: null
        },
        hideFooter: false
      }
    }
  },
  computed: {
    tableColumns () {
      const columns = [
        {
          width: 200,
          label: this.$t(`${this.i18nPrefix}.label.job`),
          dataKey: 'job',
          slotName: 'job',
          showOverflowTooltip: true
        },
        {
          width: 100,
          label: this.$t(`${this.i18nPrefix}.label.postChannel`),
          dataKey: 'channel',
          slotName: 'channel'
        },
        {
          width: 60,
          label: this.$t(`${this.i18nPrefix}.label.action`),
          dataKey: 'operation_type',
          slotName: 'channelAction'
        },
        {
          width: 100,
          label: this.$t(`${this.i18nPrefix}.label.taskDate`),
          dataKey: 'created_time',
          formatter: val => moment(val).format('YYYY-MM-DD')
        },
        {
          width: 100,
          label: this.$t(`${this.i18nPrefix}.label.postStatus`),
          dataKey: 'status',
          slotName: 'status'
        },
        {
          width: 100,
          label: this.$t(`${this.i18nPrefix}.label.taskFinishDate`),
          dataKey: 'finished_time',
          formatter: val => val ? moment(val).format('YYYY-MM-DD') : this.$t(`${this.i18nPrefix}.label.publishing`),
          showOverflowTooltip: true
        },
        {
          width: 200,
          label: this.$t(`${this.i18nPrefix}.label.jobAffiliatedCompany`),
          dataKey: 'company',
          formatter: (val, row) => `${getI18nPropertyValue(row.company, 'cn_name', 'name')}${isEn() ? ',' : '，'} ID${row.company.id}`
        },
        {
          width: 100,
          label: this.$t(`${this.i18nPrefix}.label.jobCloseDate`),
          dataKey: 'jobCloseDate',
          formatter: (val, row) => moment(row.job.expire_date).format('YYYY-MM-DD'),
          showOverflowTooltip: true
        }
      ]
      return columns
    },
    postStatusOptions () {
      return [
        {
          label: this.$t(`${this.i18nPrefix}.label.open`),
          value: 'PENDING'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.finished`),
          value: 'FINISHED'
        },
        {
          label: this.$t(`${this.i18nPrefix}.label.all`),
          value: 'ALL'
        }
      ]
    },
    displayJobPostingList () {
      const jobIdInfoMap = {}
      const result = []
      _.forEach(this.jobPostingList, task => {
        const jobId = task.job.id
        if (_.has(jobIdInfoMap, jobId)) {
          jobIdInfoMap[jobId].children.push(task)
        } else {
          const info = {
            id: `${task.id}_${task.job.id}`,
            company: task.company,
            job: task.job,
            children: [task]
          }
          result.push(info)
          jobIdInfoMap[jobId] = info
        }
      })
      return result
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('jobPosting', ['getJobPostingTaskList', 'updateJobPostingTaskStatus']),
    async initPage () {
      await this.loadJobPostingList()
    },
    async loadJobPostingList (reload) {
      if (reload) {
        this.pagination.page = 1
        this.pagination.total = 0
      }
      const params = {
        keywords: this.search.keywords,
        post_status: this.search.postStatus,
        page: this.pagination.page,
        page_size: this.pagination.pageSize
      }
      if (this.search.companyId) {
        params.company_id = this.search.companyId
      }
      this.loading = true
      try {
        const resp = await this.getJobPostingTaskList(params)
        if (resp) {
          this.pagination.total = resp.total
          this.jobPostingList = resp.result
          this.companyOptions = _.chain(resp.company_list)
            .filter(item => !item.parent_id && !item.root_company_id)
            .flatMapDeep(root => {
              addI18nProperty(root, 'i18nName', 'cn_name', 'name')
              root.level = 1
              const children = this.formatCompanies(resp.company_list, root.id, 2)
              return [root, children]
            }).value()
        }
      } finally {
        this.loading = false
      }
    },
    handleSpanMethod ({ row, column, rowIndex, columnIndex }) {
      if (row.children && row.children.length > 0) {
        if (columnIndex === 1) {
          return [1, 4]
        } else if (columnIndex === 2 || columnIndex === 3 || columnIndex === 4) {
          return [1, 0]
        } else {
          return [1, 1]
        }
      }
    },
    formatCompanies (companies, parentId, level) {
      const children = _.filter(companies, item => item.parent_id === parentId)
      return _.flatMapDeep(children, child => {
        addI18nProperty(child, 'i18nName', 'cn_name', 'name')
        child.level = level
        const descendants = this.formatCompanies(companies, child.id, level + 1)
        return [child, descendants]
      })
    },
    showJobTaskItemDialog (row) {
      this.jobPostingDetailDialog.show = true
      this.jobPostingDetailDialog.jobTitle = `${getI18nPropertyValue(row.job, 'cn_title', 'title')} ID${row.job.id}`
      this.jobPostingDetailDialog.companyTitle = `${getI18nPropertyValue(row.company, 'cn_name', 'name')} ID${row.company.id}`
      this.jobPostingDetailDialog.hideFooter = row.status === JOB_POSTING_STATUS.FINISHED
      row.is_finished = row.status === JOB_POSTING_STATUS.FINISHED
      this.jobPostingDetailDialog.task = {
        ...row
      }
    },
    async handleSave () {
      try {
        const jobPostingTask = this.jobPostingDetailDialog.task
        const params = {
          job_posting_task_id: jobPostingTask.id,
          task_item_info: jobPostingTask
        }
        const resp = await this.updateJobPostingTaskStatus(params)
        if (resp) {
          let reload = false
          if (resp.status === JOB_POSTING_STATUS.FINISHED && this.isLastPageLastData() && this.search.postStatus === '1') {
            reload = true
          }
          // 当搜索条件中有指定状态时，不刷新数据, 只将更新的数据移除
          if (this.search.postStatus !== 'ALL') {
            const taskId = jobPostingTask.id
            // 删除 taskId 对应的数据
            this.jobPostingList.splice(_.findIndex(this.jobPostingList, item => item.id === taskId), 1)
          } else {
            this.loadJobPostingList(reload)
          }

          this.$message({
            type: 'success',
            message: this.$t('common.message.saveSuccess')
          })
        }
      } finally {
        this.jobPostingDetailDialog.show = false
      }
    },
    isLastPageLastData () {
      const { page, pageSize, total } = this.pagination

      const totalPages = _.ceil(total / pageSize)
      const remainder = total % pageSize

      return page === totalPages && remainder === 1
    },
    formatJob (row) {
      return `${getI18nPropertyValue(row.job, 'cn_title', 'title')}${isEn() ? ',' : '，'} ID${row.job.id}`
    },
    jobPath (row) {
      return `/v-admin/job/edit/${row.job.id}?back=${encodeURI(this.$route.path)}`
    },
    handleKeywordsChange () {
      if (!this.keywordsSearchDebounce) {
        this.keywordsSearchDebounce = _.debounce(() => this.loadJobPostingList(true), 1000)
      }
      this.keywordsSearchDebounce()
    }
  }
}
</script>

<style lang="scss" scoped>
.job-posting-management-container {
  .job-posting-list {
    .status {
      cursor: pointer;
    }
    .task-status-open {
      color: #ea6d3c;
    }
    .task-status-finished {
      color: #009245;
    }
    .iconxiangxiadejiantou {
      position: relative;
      margin-left: 4px;
      font-size: 25px;
      top: 4px;
    }
    .job-link {
      color: #606266;
    }

    ::v-deep {
      .el-table {
        .expanded {
          .job-link {
            color: #0096cc;
          }
        }
      }
    }

    .channel-container {
      .channel-icon {
        width: 80px;
        height: 30px;
        background-repeat: no-repeat;
        background-position: center;
        background-size: 100%;

        &.channel-ajinga {
          background-image: url(~@/assets/image/postingChannel/ajinga_cn.png);
        }

        &.channel-51job {
          background-image: url(~@/assets/image/postingChannel/51job.png);
        }

        &.channel-wotui {
          background-image: url(~@/assets/image/postingChannel/wotui.png);
        }

        &.channel-zhaopin {
          background-image: url(~@/assets/image/postingChannel/zhaopin.png);
        }

        &.channel-liepin {
          background-image: url(~@/assets/image/postingChannel/liepin.png);
        }

        &.channel-58 {
          background-image: url(~@/assets/image/postingChannel/58.png);
        }

        &.channel-ganji {
          background-image: url(~@/assets/image/postingChannel/ganji.png);
        }

        &.channel-veryeast {
          background-image: url(~@/assets/image/postingChannel/veryeast.png);
        }

        &.channel-shixiseng {
          background-image: url(~@/assets/image/postingChannel/shixiseng.png);
        }

        &.channel-linkedin {
          background-image: url(~@/assets/image/postingChannel/linkedin.png);
        }

        &.channel-jobstreet {
          background-image: url(~@/assets/image/postingChannel/jobstreet.png);
        }

        &.channel-jobsdb {
          background-image: url(~@/assets/image/postingChannel/jobsdb.png);
        }

        &.channel-zzjob88 {
          background-image: url(~@/assets/image/postingChannel/zzjob88.png);
        }

        &.channel-104 {
          background-image: url(~@/assets/image/postingChannel/104.jpg);
          background-size: 130%;
          background-position: -25px 50%;
        }

        &.channel-indeed {
          background-image: url(~@/assets/image/postingChannel/indeed.jpg);
        }

        &.channel-parttime {
          background-image: url(~@/assets/image/postingChannel/parttime.png);
          background-size: unset;
        }

        &.channel-lagou {
          background-image: url(~@/assets/image/postingChannel/lagou.png);
          background-size: 90%;
        }

        &.channel-boss {
          background-image: url(~@/assets/image/postingChannel/boss.png);
          background-size: 90%;
        }

        &.channel-hrol {
          background-image: url(~@/assets/image/postingChannel/hrol.png);
          background-size: 90%;
        }

        &.channel-91job {
          background-image: url(~@/assets/image/postingChannel/91job.png);
          background-size: 90%;
        }

        &.channel-yimaitongdao {
          background-image: url(~@/assets/image/postingChannel/yimaitongdao.svg);
        }

        &.channel-liepin_agency {
          background-image: url(~@/assets/image/postingChannel/liepin_agency.png);
        }

        &.channel-maimai {
          background-image: url(~@/assets/image/postingChannel/maimai.png);
          background-size: 90%;
        }

        &.channel-gooood {
          background-image: url(~@/assets/image/postingChannel/gooood.jpg);
          background-size: 90%;
        }
      }
    }
    .op-icon {
      cursor: pointer;
      width: 25px;
      height: 17px;
      background-image: url($baseDomain + 'static/image/job_list_sprite.png?v=2018100801');
      margin-right: 15px;
      &.op-status-ADD {
        background-position: -37px -362px;
      }
      &.op-status-DELETE {
        background-position: -136px -362px;
      }
      &.op-status-UPDATE {
        background-position: -87px -362px;
      }
    }
  }
  .job-task-item-container {
    .job-task-item {
      display: flex;
      justify-content: left;
      align-items: center;
      margin-top: 10px;
      .op-icon {
        width: 25px;
        height: 17px;
        background-image: url($baseDomain + 'static/image/job_list_sprite.png?v=2018100801');
        margin-right: 15px;
        &.op-status-ADD {
          background-position: -37px -362px;
        }
        &.op-status-DELETE {
          background-position: -136px -362px;
        }
        &.op-status-UPDATE {
          background-position: -87px -362px;
        }
      }

      .channel-icon {
        width: 80px;
        height: 30px;
        background-repeat: no-repeat;
        background-position: center;
        background-size: 100%;

        &.channel-ajinga {
          background-image: url(~@/assets/image/postingChannel/ajinga_cn.png);
        }

        &.channel-51job {
          background-image: url(~@/assets/image/postingChannel/51job.png);
        }

        &.channel-wotui {
          background-image: url(~@/assets/image/postingChannel/wotui.png);
        }

        &.channel-zhaopin {
          background-image: url(~@/assets/image/postingChannel/zhaopin.png);
        }

        &.channel-liepin {
          background-image: url(~@/assets/image/postingChannel/liepin.png);
        }

        &.channel-58 {
          background-image: url(~@/assets/image/postingChannel/58.png);
        }

        &.channel-ganji {
          background-image: url(~@/assets/image/postingChannel/ganji.png);
        }

        &.channel-veryeast {
          background-image: url(~@/assets/image/postingChannel/veryeast.png);
        }

        &.channel-shixiseng {
          background-image: url(~@/assets/image/postingChannel/shixiseng.png);
        }

        &.channel-linkedin {
          background-image: url(~@/assets/image/postingChannel/linkedin.png);
        }

        &.channel-jobstreet {
          background-image: url(~@/assets/image/postingChannel/jobstreet.png);
        }

        &.channel-jobsdb {
          background-image: url(~@/assets/image/postingChannel/jobsdb.png);
        }

        &.channel-zzjob88 {
          background-image: url(~@/assets/image/postingChannel/zzjob88.png);
        }

        &.channel-104 {
          background-image: url(~@/assets/image/postingChannel/104.jpg);
          background-size: 130%;
          background-position: -25px 50%;
        }

        &.channel-indeed {
          background-image: url(~@/assets/image/postingChannel/indeed.jpg);
        }

        &.channel-parttime {
          background-image: url(~@/assets/image/postingChannel/parttime.png);
          background-size: unset;
        }

        &.channel-lagou {
          background-image: url(~@/assets/image/postingChannel/lagou.png);
          background-size: 90%;
        }

        &.channel-boss {
          background-image: url(~@/assets/image/postingChannel/boss.png);
          background-size: 90%;
        }

        &.channel-hrol {
          background-image: url(~@/assets/image/postingChannel/hrol.png);
          background-size: 90%;
        }

        &.channel-91job {
          background-image: url(~@/assets/image/postingChannel/91job.png);
          background-size: 90%;
        }

        &.channel-yimaitongdao {
          background-image: url(~@/assets/image/postingChannel/yimaitongdao.svg);
        }

        &.channel-liepin_agency {
          background-image: url(~@/assets/image/postingChannel/liepin_agency.png);
        }

        &.channel-maimai {
          background-image: url(~@/assets/image/postingChannel/maimai.png);
          background-size: 90%;
        }

        &.channel-gooood {
          background-image: url(~@/assets/image/postingChannel/gooood.jpg);
          background-size: 90%;
        }
      }

      .third-party-link-input {
        margin-left: 20px;
        margin-right: 25px;
        height: 33px;
      }

      .channel-status {
        scale: 2;
        margin-top: -1px;
      }
    }
  }
}
</style>
